import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ManageBlog = () => {
  const [posts, setPosts] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    author: "",
    image_url: "",
    seo_title: "",
    seo_keywords: "",
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle paste event to auto-format content
  const handlePaste = (e) => {
    e.preventDefault();
    
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedText = clipboardData.getData('text/plain');
    
    if (!pastedText) return;
    
    // Auto-format pasted content
    const lines = pastedText.split('\n');
    const formattedLines = lines.map(line => {
      const trimmedLine = line.trim();
      
      // Skip empty lines
      if (!trimmedLine) return '';
      
      // Detect if line looks like a heading (all caps, short, or ends with colon)
      const isAllCaps = trimmedLine === trimmedLine.toUpperCase() && trimmedLine.length < 50 && /[A-Z]/.test(trimmedLine);
      const endsWithColon = trimmedLine.endsWith(':');
      const isShortAndBold = trimmedLine.length < 60 && !trimmedLine.includes('.');
      
      // Convert to markdown headings
      if (isAllCaps || (endsWithColon && trimmedLine.length < 60)) {
        return `## ${trimmedLine.replace(':', '')}`;
      } else if (isShortAndBold && lines.indexOf(line) < 3) {
        // First few short lines might be main headings
        return `# ${trimmedLine}`;
      } else if (trimmedLine.startsWith('-') || trimmedLine.startsWith('•') || trimmedLine.startsWith('*')) {
        // Already a list item
        return `- ${trimmedLine.substring(1).trim()}`;
      } else if (/^\d+\./.test(trimmedLine)) {
        // Numbered list
        return trimmedLine;
      } else {
        // Regular paragraph
        return trimmedLine;
      }
    });
    
    const formattedText = formattedLines.join('\n\n');
    
    // Insert at cursor position
    const textarea = e.target;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    const newContent = formData.content.substring(0, start) + formattedText + formData.content.substring(end);
    setFormData({ ...formData, content: newContent });
    
    // Set cursor position after pasted content
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + formattedText.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  // Helper function to insert formatting at cursor position
  const insertFormatting = (formatType) => {
    const textarea = document.getElementById('content');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    let formattedText = '';
    
    switch(formatType) {
      case 'h1':
        formattedText = `\n# ${selectedText || 'Heading 1'}\n`;
        break;
      case 'h2':
        formattedText = `\n## ${selectedText || 'Heading 2'}\n`;
        break;
      case 'h3':
        formattedText = `\n### ${selectedText || 'Heading 3'}\n`;
        break;
      case 'bold':
        formattedText = `**${selectedText || 'bold text'}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText || 'italic text'}*`;
        break;
      case 'list':
        formattedText = `\n- ${selectedText || 'List item'}\n`;
        break;
      default:
        return;
    }

    const newContent = formData.content.substring(0, start) + formattedText + formData.content.substring(end);
    setFormData({ ...formData, content: newContent });
    
    // Refocus textarea
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + formattedText.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, formData, { headers });
        toast.success("Blog post updated successfully!");
      } else {
        await axios.post(`${API}/blog`, formData, { headers });
        toast.success("Blog post created successfully!");
      }

      setIsDialogOpen(false);
      setEditingPost(null);
      setFormData({ title: "", excerpt: "", content: "", author: "", image_url: "", seo_title: "", seo_keywords: "" });
      fetchPosts();
    } catch (error) {
      console.error("Error saving blog post:", error);
      toast.error("Failed to save blog post");
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      author: post.author,
      image_url: post.image_url || "",
      seo_title: post.seo_title || "",
      seo_keywords: post.seo_keywords || "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog post?")) return;

    const token = localStorage.getItem("adminToken");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      await axios.delete(`${API}/blog/${id}`, { headers });
      toast.success("Blog post deleted successfully!");
      fetchPosts();
    } catch (error) {
      console.error("Error deleting blog post:", error);
      toast.error("Failed to delete blog post");
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingPost(null);
    setFormData({ title: "", excerpt: "", content: "", author: "", image_url: "", seo_title: "", seo_keywords: "" });
  };

  return (
    <div data-testid="manage-blog">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Manage Blog</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-post-button">
              <Plus size={20} className="mr-2" /> Add Post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="blog-dialog">
            <DialogHeader>
              <DialogTitle>{editingPost ? "Edit Blog Post" : "Add New Blog Post"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  name="title"
                  data-testid="blog-title-input"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="author">Author</Label>
                <Input
                  id="author"
                  name="author"
                  data-testid="blog-author-input"
                  value={formData.author}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="excerpt">Excerpt</Label>
                <Textarea
                  id="excerpt"
                  name="excerpt"
                  rows={2}
                  data-testid="blog-excerpt-input"
                  value={formData.excerpt}
                  onChange={handleChange}
                  placeholder="Brief summary of the post..."
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="image_url">Banner Image URL (Optional)</Label>
                <Input
                  id="image_url"
                  name="image_url"
                  data-testid="blog-image-input"
                  value={formData.image_url}
                  onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a banner image URL for your blog post
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="content">Content</Label>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => insertFormatting('h1')}
                      title="Heading 1"
                      className="px-2 py-1 h-7 text-xs"
                    >
                      H1
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => insertFormatting('h2')}
                      title="Heading 2"
                      className="px-2 py-1 h-7 text-xs"
                    >
                      H2
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => insertFormatting('h3')}
                      title="Heading 3"
                      className="px-2 py-1 h-7 text-xs"
                    >
                      H3
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => insertFormatting('bold')}
                      title="Bold"
                      className="px-2 py-1 h-7 text-xs font-bold"
                    >
                      B
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => insertFormatting('italic')}
                      title="Italic"
                      className="px-2 py-1 h-7 text-xs italic"
                    >
                      I
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => insertFormatting('list')}
                      title="List"
                      className="px-2 py-1 h-7 text-xs"
                    >
                      •
                    </Button>
                  </div>
                </div>
                <Textarea
                  id="content"
                  name="content"
                  rows={12}
                  data-testid="blog-content-input"
                  value={formData.content}
                  onChange={handleChange}
                  onPaste={handlePaste}
                  placeholder="Write or paste your blog post content here. Headings will be auto-detected when you paste!"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 <strong>Auto-Format on Paste:</strong> ALL CAPS text becomes H2, short lines become H1, bullets auto-convert to lists. Or use buttons/markdown: # H1, ## H2, ### H3, **bold**, *italic*, - lists
                </p>
              </div>

              {/* SEO Section */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">SEO Settings (Optional)</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="seo_title">SEO Title</Label>
                    <Input
                      id="seo_title"
                      name="seo_title"
                      data-testid="blog-seo-title-input"
                      value={formData.seo_title}
                      onChange={handleChange}
                      placeholder="Custom title for search engines (leave empty to use post title)"
                      maxLength={60}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.seo_title.length}/60 characters
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="seo_keywords">SEO Keywords</Label>
                    <Input
                      id="seo_keywords"
                      name="seo_keywords"
                      data-testid="blog-seo-keywords-input"
                      value={formData.seo_keywords}
                      onChange={handleChange}
                      placeholder="e.g., cocktail recipes, bartending tips, event planning"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Comma-separated keywords for better search visibility
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <Button type="submit" className="flex-1" data-testid="blog-submit-button">
                  {editingPost ? "Update" : "Create"} Post
                </Button>
                <Button type="button" variant="outline" onClick={handleDialogClose} data-testid="blog-cancel-button">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post, index) => (
          <Card key={post.id} data-testid={`blog-card-${index}`}>
            <CardHeader>
              <CardTitle className="text-lg">{post.title}</CardTitle>
              <p className="text-sm text-gray-600">By {post.author}</p>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(post)}
                  data-testid={`edit-post-${index}`}
                >
                  <Pencil size={16} className="mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(post.id)}
                  data-testid={`delete-post-${index}`}
                >
                  <Trash2 size={16} className="mr-1" /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {posts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600">No blog posts yet. Click "Add Post" to create one.</p>
        </div>
      )}
    </div>
  );
};

export default ManageBlog;
